/**
 * Device Detection Utility
 * Detects operating system, browser, device type, and user agent information
 */

/**
 * Detect the operating system
 * @returns {string} Operating system name
 */
export const detectOS = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'Unknown'
  }

  const ua = navigator.userAgent
  const platform = navigator.platform

  // iOS detection
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return 'iOS'
  }

  // Android detection
  if (/Android/.test(ua)) {
    return 'Android'
  }

  // macOS detection (including iPadOS 13+)
  if (/Mac/.test(platform) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'macOS'
  }

  // Windows detection
  if (/Win/.test(platform)) {
    return 'Windows'
  }

  // Linux detection
  if (/Linux/.test(platform)) {
    return 'Linux'
  }

  // Other platforms
  return platform || 'Unknown'
}

/**
 * Detect the browser name and version
 * @returns {string} Browser name
 */
export const detectBrowser = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'Unknown'
  }

  const ua = navigator.userAgent

  // Edge (Chromium-based)
  if (/Edg/.test(ua)) {
    return 'Edge'
  }

  // Chrome
  if (/Chrome/.test(ua) && !/Edg/.test(ua)) {
    return 'Chrome'
  }

  // Safari (must be checked after Chrome as Chrome also includes Safari in UA)
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    return 'Safari'
  }

  // Firefox
  if (/Firefox/.test(ua)) {
    return 'Firefox'
  }

  // Opera
  if (/Opera|OPR/.test(ua)) {
    return 'Opera'
  }

  // Internet Explorer
  if (/MSIE|Trident/.test(ua)) {
    return 'Internet Explorer'
  }

  return 'Unknown'
}

/**
 * Detect device type (mobile, tablet, or desktop)
 * Uses comprehensive detection logic including touch support and screen size
 * @returns {'mobile' | 'tablet' | 'desktop'} Device type
 */
export const detectDeviceType = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'desktop'
  }

  const ua = navigator.userAgent.toLowerCase()
  
  // Phone indicators
  const isPhone = /iphone|ipod|android.*mobile|windows phone|blackberry|bb10|opera mini|mobile|mobi/.test(ua)
  
  // Tablet indicators (includes Android tablets, iPad, generic 'tablet')
  const isTabletUA = /ipad|tablet/.test(ua) || /android(?!.*mobile)/.test(ua)
  
  // iPadOS 13+ reports Mac-like UA; detect by touch points on Mac platform
  const isIpadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  
  // Fallback: touch + small screen (useful for some edge cases)
  const isSmallTouchScreen = (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) &&
                             Math.min(screen.width, screen.height) <= 1024

  if (isPhone) {
    return 'mobile'
  }
  
  if (isTabletUA || isIpadOS) {
    return 'tablet'
  }
  
  if (isSmallTouchScreen) {
    return 'tablet'
  }

  return 'desktop'
}

/**
 * Get the full user agent string
 * @returns {string} User agent string
 */
export const getUserAgent = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'Unknown'
  }
  
  return navigator.userAgent
}

/**
 * Get client IP address (best effort from available sources)
 * Note: This returns null on client-side. IP should be captured server-side.
 * @returns {string | null} IP address or null
 */
export const getClientIP = () => {
  // IP address cannot be reliably obtained from client-side JavaScript
  // It should be captured on the server-side (WordPress)
  return null
}

/**
 * Get all device information in one object
 * @returns {Object} Complete device information
 */
export const getDeviceInfo = () => {
  return {
    os: detectOS(),
    browser: detectBrowser(),
    deviceType: detectDeviceType(),
    userAgent: getUserAgent(),
    ip: getClientIP(), // Will be null, should be populated server-side
  }
}

/**
 * Get device info as formatted string for logging
 * @returns {string} Formatted device info
 */
export const getDeviceInfoString = () => {
  const info = getDeviceInfo()
  return `${info.os} | ${info.browser} | ${info.deviceType}`
}

export default {
  detectOS,
  detectBrowser,
  detectDeviceType,
  getUserAgent,
  getClientIP,
  getDeviceInfo,
  getDeviceInfoString,
}
